import React, { useState, useEffect } from 'react';
import { getRunningSiteVisits } from '../../../services/private/site-visit.service';
import SetOutTimeModal from './SetOutTimeModal';
import { ReactComponent as RefreshIcon } from '../../../assets/plus_icon/refresh.svg';

const ActiveSiteVisitsModal = ({ show, closeModal, onCountUpdate, projectId }) => {
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [showSetOutTimeModal, setShowSetOutTimeModal] = useState(false);
    const isMountedRef = React.useRef(true);

    const fetchVisits = async () => {
        setLoading(true);
        try {
            const payload = { pageNumber: 1, perPage: 300 };
            if (projectId) {
                payload.projectId = projectId;
                payload.project_id = projectId;
            }
            const res = await getRunningSiteVisits(payload);
            if (!isMountedRef.current) return;
            if (res?.data?.status === 200 || res?.status === 200) {
                const list = res?.data?.data || res?.data || [];
                setVisits(Array.isArray(list) ? list : []);
                if (onCountUpdate) {
                    onCountUpdate(Array.isArray(list) ? list.length : 0);
                }
            }
        } catch (err) {
            console.error("Failed to fetch running site visits", err);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        isMountedRef.current = true;
        if (show) {
            fetchVisits();
        }
        return () => {
            isMountedRef.current = false;
        };
    }, [show]);

    const handleOpenSetOutTime = (visit) => {
        setSelectedVisit(visit);
        setShowSetOutTimeModal(true);
    };

    const handleOutTimeSuccess = (uuid) => {
        setVisits(prev => {
            const updated = prev.filter(v => v.uuid !== uuid);
            if (onCountUpdate) {
                onCountUpdate(updated.length);
            }
            return updated;
        });
    };

    const getInitials = (name) => {
        if (!name) return 'CU';
        const parts = name.trim().split(' ').filter(Boolean);
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const formatInTime = (isoString) => {
        if (!isoString) return 'N/A';
        try {
            const date = new Date(isoString);
            const timeStr = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
            const dateStr = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
            return `${dateStr} • ${timeStr}`;
        } catch (e) {
            return isoString;
        }
    };

    const getOnSiteDuration = (inTimeISO) => {
        if (!inTimeISO) return { text: 'N/A', isOverdue: false, isWarning: false };
        try {
            const inTime = new Date(inTimeISO).getTime();
            const now = Date.now();
            const diffMs = Math.max(0, now - inTime);
            const totalMinutes = Math.floor(diffMs / (1000 * 60));
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            
            let text = '';
            if (hours > 0) {
                text = `${hours}h ${minutes}m`;
            } else {
                text = `${minutes}m`;
            }
            
            return {
                text,
                isOverdue: hours >= 2,
                isWarning: hours >= 1 && hours < 2,
            };
        } catch (e) {
            return { text: 'N/A', isOverdue: false, isWarning: false };
        }
    };

    const getClosingManagerName = (visit) => {
        if (!visit) return 'Unassigned';
        if (visit.closing_user_name) return visit.closing_user_name;
        if (visit.closing_manager_name) return visit.closing_manager_name;
        if (visit.closing_user && typeof visit.closing_user === 'string') return visit.closing_user;
        if (visit.closing_manager && typeof visit.closing_manager === 'string') return visit.closing_manager;
        if (visit.closing_user?.name) return visit.closing_user.name;
        if (visit.closing_user?.first_name) return `${visit.closing_user.first_name} ${visit.closing_user.last_name || ''}`.trim();
        if (visit.closing_manager?.name) return visit.closing_manager.name;
        if (visit.manager_name) return visit.manager_name;
        if (visit.cm_name) return visit.cm_name;
        if (visit.assigned_to_name) return visit.assigned_to_name;
        if (visit.user_name) return visit.user_name;
        return 'Unassigned';
    };

    if (!show) return null;

    return (
        <>
            <style>{`
                @keyframes modalSpin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .spin-animation-modal {
                    animation: modalSpin 1s linear infinite;
                }
            `}</style>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-3 sm:p-4 overflow-y-auto">
                {/* Modal Container */}
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-[946px] max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in duration-150 my-auto">
                    
                    {/* Header: Optimized & Compact for Mobile */}
                    <div className="flex items-center justify-between px-3.5 py-3 sm:px-6 sm:py-4 border-b border-gray-100 bg-white flex-shrink-0">
                        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl bg-[#F0EEFF] text-[#635BFF] flex items-center justify-center flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm:w-[22px] sm:h-[22px]">
                                    <line x1="18" y1="20" x2="18" y2="10" />
                                    <line x1="12" y1="20" x2="12" y2="4" />
                                    <line x1="6" y1="20" x2="6" y2="14" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5 sm:gap-2">
                                    <h2 className="text-[17px] sm:text-[23px] font-bold text-gray-900 mb-0 truncate">Customers currently on site</h2>
                                    <button
                                        type="button"
                                        onClick={fetchVisits}
                                        disabled={loading}
                                        className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl border border-gray-200 bg-white hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center flex-shrink-0"
                                        title="Refresh list"
                                    >
                                        <RefreshIcon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-700 transform scale-x-[-1] ${loading ? 'spin-animation-modal' : ''}`} />
                                    </button>
                                </div>
                                <p className="text-[11px] sm:text-xs text-gray-400 mb-0 mt-0.5 truncate hidden sm:block">Tap "Set Out Time" to open the check-out form for that visit</p>
                            </div>
                        </div>

                        <button
                            onClick={closeModal}
                            className="w-7 h-7 sm:w-9 sm:h-9 border border-gray-200 rounded-lg sm:rounded-xl bg-white hover:bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-2"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>

                    {/* Content List */}
                    <div className="px-4 py-3 sm:px-6 sm:py-4 overflow-y-auto flex-1 bg-white space-y-3">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#635BFF] mb-2.5"></div>
                                <p className="text-xs sm:text-sm font-medium">Loading active site visits...</p>
                            </div>
                        ) : visits.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="w-14 h-14 rounded-full bg-[#F0EEFF] text-[#635BFF] flex items-center justify-center mb-2.5">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                                        <line x1="9" y1="9" x2="9.01" y2="9" />
                                        <line x1="15" y1="9" x2="15.01" y2="9" />
                                    </svg>
                                </div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-1">No Active Site Visits</h4>
                                <p className="text-xs text-gray-500 max-w-xs">All site visits currently have their out times recorded.</p>
                            </div>
                        ) : (
                            visits.map((visit) => {
                                const durationInfo = getOnSiteDuration(visit.client_in_time);
                                const mgrName = getClosingManagerName(visit);

                                return (
                                    <div
                                        key={visit.uuid || visit.id}
                                        className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-3.5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex flex-col lg:flex-row lg:items-center justify-between gap-3"
                                    >
                                        {/* --- DESKTOP VIEW (lg: flex row with dividers) --- */}
                                        <div className="hidden lg:flex lg:items-center lg:justify-between lg:w-full lg:gap-4">
                                            {/* Col 1: Customer Initials + Name + Mobile */}
                                            <div className="flex items-center gap-3 min-w-[190px] flex-shrink-0">
                                                <div className="w-10 h-10 rounded-full bg-[#EBF3FF] text-[#3B82F6] font-bold text-xs sm:text-sm flex items-center justify-center flex-shrink-0 shadow-xs">
                                                    {getInitials(visit.customer_name)}
                                                </div>
                                                <div className="flex flex-col justify-center min-w-0 max-w-[160px] relative group/name">
                                                    <span 
                                                        className="font-bold text-gray-900 text-sm sm:text-base leading-tight truncate cursor-default"
                                                    >
                                                        {visit.customer_name || 'Unnamed Customer'}
                                                    </span>

                                                    {/* Custom Sleek Hover Tooltip/Popover for Full Name */}
                                                    {visit.customer_name && visit.customer_name.length > 15 && (
                                                        <div className="absolute left-0 bottom-full mb-1.5 hidden group-hover/name:flex items-center justify-center z-[10000] pointer-events-none transition-all duration-200">
                                                            <div className="bg-gray-900 text-white text-xs font-medium py-1.5 px-3 rounded-lg shadow-xl whitespace-nowrap border border-gray-700 relative">
                                                                {visit.customer_name}
                                                                {/* Arrow */}
                                                                <div className="absolute top-full left-4 -mt-[1px] border-4 border-transparent border-t-gray-900"></div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {visit.customer_mobile && (
                                                        <span className="text-xs font-semibold text-[#3B82F6] leading-tight block mt-0.5">
                                                            {visit.customer_mobile}
                                                        </span>
                                                    )}

                                                    {/* OVERDUE Tag under mobile for laptop view */}
                                                    {durationInfo.isOverdue && (
                                                        <div className="mt-1">
                                                            <span 
                                                                className="text-[9px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 uppercase tracking-wider shadow-xs whitespace-nowrap"
                                                                style={{ backgroundColor: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' }}
                                                            >
                                                                <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626] animate-pulse"></span>
                                                                OVERDUE
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Vertical Separator 1 */}
                                            <div className="w-[1px] h-8 bg-gray-200 self-center flex-shrink-0" />

                                            {/* Col 2: Closing Manager */}
                                            <div className="min-w-[120px] flex flex-col justify-center">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                                    CLOSING MANAGER
                                                </span>
                                                <div className="flex items-center gap-1.5 h-6">
                                                    <div className="w-5 h-5 rounded-full bg-gray-100 text-gray-600 text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                                                        {getInitials(mgrName)}
                                                    </div>
                                                    <span className="text-xs font-semibold text-gray-800 truncate max-w-[110px] block" title={mgrName}>
                                                        {mgrName}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Vertical Separator 2 */}
                                            <div className="w-[1px] h-8 bg-gray-200 self-center flex-shrink-0" />

                                            {/* Col 3: Check-in Time */}
                                            <div className="min-w-[130px] flex flex-col justify-center">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                                    CHECK-IN TIME
                                                </span>
                                                <div className="flex items-center h-6">
                                                    <span className="text-xs font-semibold text-gray-800 leading-none whitespace-nowrap">
                                                        {formatInTime(visit.client_in_time)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Vertical Separator 3 */}
                                            <div className="w-[1px] h-8 bg-gray-200 self-center flex-shrink-0" />

                                            {/* Col 4: On Site Duration */}
                                            <div className="min-w-[100px]">
                                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                                    ON SITE FOR
                                                </span>
                                                <div className="flex items-center h-6">
                                                    <div className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${
                                                        durationInfo.isOverdue
                                                            ? 'bg-red-50 text-red-600 border-red-200'
                                                            : durationInfo.isWarning
                                                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                            : 'bg-slate-50 text-slate-700 border-slate-200'
                                                    }`}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                            <circle cx="12" cy="12" r="10" />
                                                            <polyline points="12 6 12 12 16 14" />
                                                        </svg>
                                                        <span>{durationInfo.text}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Vertical Separator 4 */}
                                            <div className="w-[1px] h-8 bg-gray-200 self-center flex-shrink-0" />

                                            {/* Action Button */}
                                            <button
                                                type="button"
                                                onClick={() => handleOpenSetOutTime(visit)}
                                                className="px-4 py-2.5 bg-gradient-to-r from-[#635BFF] to-[#3B37F5] hover:opacity-95 text-white rounded-xl font-semibold text-xs transition-all shadow-md flex items-center justify-center gap-1.5 active:scale-95 flex-shrink-0"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <polyline points="12 6 12 12 16 14" />
                                                </svg>
                                                Set Out Time
                                            </button>
                                        </div>

                                        {/* --- TABLET VIEW ONLY (sm: 640px to lg: 1024px) --- */}
                                        <div className="hidden sm:flex sm:flex-col sm:gap-2.5 lg:hidden">
                                            {/* Row Top: Avatar + Name & Mobile (Left) AND Set Out Time Button (Right) */}
                                            <div className="flex items-center justify-between gap-3 w-full">
                                                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                    <div className="w-10 h-10 rounded-full bg-[#EBF3FF] text-[#3B82F6] font-bold text-xs sm:text-sm flex items-center justify-center flex-shrink-0 shadow-xs">
                                                        {getInitials(visit.customer_name)}
                                                    </div>
                                                    <div className="flex flex-col justify-center min-w-0 flex-1 relative group/name">
                                                        <span className="font-bold text-gray-900 text-xs sm:text-sm leading-tight truncate cursor-default" title={visit.customer_name || 'Unnamed Customer'}>
                                                            {visit.customer_name || 'Unnamed Customer'}
                                                        </span>

                                                        {/* Custom Tooltip */}
                                                        {visit.customer_name && visit.customer_name.length > 15 && (
                                                            <div className="absolute left-0 bottom-full mb-1 hidden group-hover/name:flex items-center justify-center z-[10000] pointer-events-none">
                                                                <div className="bg-gray-900 text-white text-[11px] font-medium py-1 px-2.5 rounded-lg shadow-xl whitespace-nowrap border border-gray-700 relative">
                                                                    {visit.customer_name}
                                                                    <div className="absolute top-full left-3 -mt-[1px] border-4 border-transparent border-t-gray-900"></div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {visit.customer_mobile && (
                                                            <span className="text-[11px] sm:text-xs font-semibold text-[#3B82F6] leading-tight block mt-0.5">
                                                                {visit.customer_mobile}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenSetOutTime(visit)}
                                                    className="px-3.5 py-2 bg-gradient-to-r from-[#635BFF] to-[#3B37F5] hover:opacity-95 text-white rounded-xl font-semibold text-xs transition-all shadow-md flex items-center justify-center gap-1.5 active:scale-95 flex-shrink-0"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="10" />
                                                        <polyline points="12 6 12 12 16 14" />
                                                    </svg>
                                                    Set Out Time
                                                </button>
                                            </div>

                                            {/* Tablet Bottom Row: Closing Manager | Check-in Time | On Site For | Status */}
                                            <div className="flex items-center justify-start gap-3.5 w-full pt-2 border-t border-gray-100">
                                                {/* Closing Manager */}
                                                <div className="flex flex-col justify-center">
                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                                        CLOSING MANAGER
                                                    </span>
                                                    <div className="flex items-center gap-1">
                                                        <div className="w-4 h-4 rounded-full bg-gray-100 text-gray-600 text-[8px] font-bold flex items-center justify-center flex-shrink-0">
                                                            {getInitials(mgrName)}
                                                        </div>
                                                        <span className="text-[11px] sm:text-xs font-semibold text-gray-800 truncate max-w-[100px]" title={mgrName}>
                                                            {mgrName}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="w-[1px] h-7 bg-gray-200 self-center flex-shrink-0" />

                                                {/* Check-in Time */}
                                                <div className="flex flex-col justify-center">
                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                                        CHECK-IN TIME
                                                    </span>
                                                    <span className="text-[11px] sm:text-xs font-semibold text-gray-800 leading-tight whitespace-nowrap">
                                                        {formatInTime(visit.client_in_time)}
                                                    </span>
                                                </div>

                                                <div className="w-[1px] h-7 bg-gray-200 self-center flex-shrink-0" />

                                                {/* On Site Duration */}
                                                <div className="flex flex-col justify-center">
                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                                        ON SITE FOR
                                                    </span>
                                                    <div className={`inline-flex items-center gap-1 text-[11px] sm:text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${
                                                        durationInfo.isOverdue
                                                            ? 'bg-red-50 text-red-600 border-red-200'
                                                            : durationInfo.isWarning
                                                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                            : 'bg-slate-50 text-slate-700 border-slate-200'
                                                    }`}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                            <circle cx="12" cy="12" r="10" />
                                                            <polyline points="12 6 12 12 16 14" />
                                                        </svg>
                                                        <span>{durationInfo.text}</span>
                                                    </div>
                                                </div>

                                                {durationInfo.isOverdue ? (
                                                    <>
                                                        <div className="w-[1px] h-7 bg-gray-200 self-center flex-shrink-0" />
                                                        <div className="flex flex-col justify-center">
                                                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                                                STATUS
                                                            </span>
                                                            <span 
                                                                className="text-[9px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 uppercase tracking-wider shadow-xs whitespace-nowrap"
                                                                style={{ backgroundColor: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' }}
                                                            >
                                                                <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626] animate-pulse"></span>
                                                                OVERDUE
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : null}
                                            </div>
                                        </div>

                                        {/* --- MOBILE ONLY VIEW (< 640px: EXACT MATCH to mobile-view.jpeg reference) --- */}
                                        <div className="flex flex-col gap-2.5 sm:hidden">
                                            {/* Row 1: Avatar + Customer Name & Mobile */}
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-[#EBF3FF] text-[#3B82F6] font-bold text-xs flex items-center justify-center flex-shrink-0 shadow-xs">
                                                    {getInitials(visit.customer_name)}
                                                </div>
                                                <div className="flex flex-col justify-center min-w-0 flex-1">
                                                    <span className="font-bold text-gray-900 text-sm leading-tight truncate" title={visit.customer_name || 'Unnamed Customer'}>
                                                        {visit.customer_name || 'Unnamed Customer'}
                                                    </span>
                                                    {visit.customer_mobile && (
                                                        <span className="text-xs font-semibold text-[#3B82F6] leading-tight block mt-0.5">
                                                            {visit.customer_mobile}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Row 2: Closing Manager (Equal py-2.5 top and bottom spacing) */}
                                            <div className="flex flex-col justify-center py-2.5 border-t border-b border-gray-100 my-1">
                                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                                    CLOSING MANAGER
                                                </span>
                                                <div className="flex items-center gap-1.5">
                                                    <div className="w-4 h-4 rounded-full bg-gray-100 text-gray-600 text-[8px] font-bold flex items-center justify-center flex-shrink-0">
                                                        {getInitials(mgrName)}
                                                    </div>
                                                    <span className="text-xs font-semibold text-gray-800 truncate" title={mgrName}>
                                                        {mgrName}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Row 3: Check-in Time & Onsite For (Space Between retained with Vertical Divider) */}
                                            <div className="flex items-center justify-between gap-2 w-full py-1">
                                                {/* Check-in Time */}
                                                <div className="flex flex-col justify-center">
                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                                        CHECK-IN TIME
                                                    </span>
                                                    <span className="text-xs font-semibold text-gray-800 leading-tight">
                                                        {formatInTime(visit.client_in_time)}
                                                    </span>
                                                </div>

                                                {/* Vertical Divider */}
                                                <div className="w-[1px] h-7 bg-gray-200 self-center flex-shrink-0" />

                                                {/* On Site Duration */}
                                                <div className="flex flex-col justify-center">
                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                                        ON SITE FOR
                                                    </span>
                                                    <div 
                                                        className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
                                                            durationInfo.isWarning
                                                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                                                : !durationInfo.isOverdue
                                                                ? 'bg-slate-50 text-slate-700 border border-slate-200'
                                                                : ''
                                                        }`}
                                                        style={durationInfo.isOverdue ? {
                                                            backgroundColor: '#FEE2E2',
                                                            color: '#991B1B',
                                                            border: '1px solid #FCA5A5'
                                                        } : {}}
                                                    >
                                                        {durationInfo.isOverdue ? (
                                                            <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626] animate-pulse"></span>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                                <circle cx="12" cy="12" r="10" />
                                                                <polyline points="12 6 12 12 16 14" />
                                                            </svg>
                                                        )}
                                                        <span>{durationInfo.text}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Row 4: Full-width Set Out Time Button (Exact match to mobile-view.jpeg) */}
                                            <button
                                                type="button"
                                                onClick={() => handleOpenSetOutTime(visit)}
                                                className="w-full py-2.5 bg-[#635BFF] hover:bg-[#5249e3] text-white rounded-xl font-semibold text-xs transition-all shadow-md flex items-center justify-center gap-1.5 active:scale-95 mt-1"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <polyline points="12 6 12 12 16 14" />
                                                </svg>
                                                Set Out Time
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}

                        {/* Bottom Footer Note Card */}
                        <div className="bg-[#F7F6FF] rounded-2xl p-3.5 sm:p-4 flex items-center gap-3 border border-[#EBE8FF] mt-3">
                            <div className="w-8 h-8 rounded-full bg-[#EBE8FF] text-[#635BFF] flex items-center justify-center flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 18h6" />
                                    <path d="M10 22h4" />
                                    <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
                                </svg>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-500 mb-0">
                                <strong className="font-semibold text-[#635BFF]">Note:</strong> Make sure to set the out time when your visit is complete.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Set Out Time Modal */}
            {showSetOutTimeModal && selectedVisit && (
                <SetOutTimeModal
                    show={showSetOutTimeModal}
                    closeModal={() => {
                        setShowSetOutTimeModal(false);
                        setSelectedVisit(null);
                    }}
                    visit={selectedVisit}
                    onSuccess={handleOutTimeSuccess}
                />
            )}
        </>
    );
};

export default ActiveSiteVisitsModal;
